import React, { useState, useEffect } from "react";
import "./Portal.css";
import { useAuth } from "../../AuthContext";
import { Navigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import upload from "../../img/arrow.png";
import { PaymentWidget } from "./PaymentWidget";
import { ReactComponent as Refresh } from "../../icons/refresh.svg";
import { ReactComponent as PlusIcon } from "../../icons/plus.svg";
import { ReactComponent as BurgerIcon } from "../../icons/burger.svg";
import { ReactComponent as CloseIcon } from "../../icons/cancel.svg";
import { Table } from "../../lv1ui";

const apiItems = [
    { title: "WhatsApp", id: 0, priceField: "price_wa" },
    { title: "Telegram", id: 1, priceField: "price_telegram" },
    { title: "IMO", id: 2, priceField: "price_imo" },
    { title: "Viber", id: 3, priceField: "price_viber" },
    { title: "KakaoTalk", id: 4, priceField: "price_kakaotalk" },
    { title: "WAButton", id: 5, priceField: "price_wabutton" }
];

const portalTabs = [
    { key: "apiMessages", label: "API Messages" },
    { key: "campaigns", label: "Campaigns" },
    { key: "createCampaign", label: "Create Campaign" },
    { key: "balance", label: "Balance" },
    { key: "settings", label: "Settings" }
];

export function Portal() {
    const { appTheme = "classic" } = useOutletContext() || {};
    const [currentPriceItem, setCurrentPriceItem] = React.useState(null);
    const [mode, setMode] = useState("manual");
    const [csvFile, setCsvFile] = useState(null);
    const [phones, setPhones] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaignId, setSelectedCampaignId] = useState("");
    const [statuses, setStatuses] = useState([]);

    const [isLoading, setLoading] = useState(false);
    const [reloading, setReloading] = useState(false);
    const [activeTab, setActiveTab] = useState("createCampaign");
    const [portalMenuOpen, setPortalMenuOpen] = useState(false);
    const [campaignChannelFilter, setCampaignChannelFilter] = useState("");

    const { accessToken, user, loading, fetchWithAuth } = useAuth();
    const { t } = useTranslation();

    const PAGE_SIZE_OPTIONS = [50, 100, 500];
    const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0,
        totalPages: 0
    });

    const [msgFilters, setMsgFilters] = useState({
        status: "",
        date_from: "",
        date_to: "",
        phone: ""
    });
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState(null);

    function buildQuery(params) {
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && String(v).trim() !== "") {
                usp.append(k, v);
            }
        });
        return usp.toString();
    }

    const getApiCampaignIdByItem = (item) => {
        switch (item.title.toLowerCase()) {
            case "whatsapp":
                return user?.api_campaign_wa_id ? String(user.api_campaign_wa_id) : "";
            case "wabutton":
                return user?.api_campaign_wabutton_id ? String(user.api_campaign_wabutton_id) : "";
            case "telegram":
                return user?.api_campaign_telegram_id ? String(user.api_campaign_telegram_id) : "";
            case "imo":
                return user?.api_campaign_imo_id ? String(user.api_campaign_imo_id) : "";
            case "viber":
                return user?.api_campaign_viber_id ? String(user.api_campaign_viber_id) : "";
            case "kakaotalk":
                return user?.api_campaign_kakaotalk_id ? String(user.api_campaign_kakaotalk_id) : "";
            default:
                return "";
        }
    };

    const getDefaultApiCampaignId = () => {
        const defaultItem = apiItems.find((item) => getApiCampaignIdByItem(item));
        return defaultItem ? getApiCampaignIdByItem(defaultItem) : "";
    };

    const fetchCampaigns = async () => {
        try {
            const res = await fetchWithAuth("/api/web/campaigns");
            const data = await res.json();
            if (data.success) setCampaigns(data.campaigns);
        } catch (err) {
            console.error("Error loading campaigns", err);
        }
    };

    const fetchStatuses = async (campaignId, page = 1, limit = DEFAULT_PAGE_SIZE) => {
        try {
            const qs = buildQuery({
                page,
                limit,
                ...msgFilters
            });

            const res = await fetchWithAuth(`/api/web/campaign/${campaignId}?${qs}`);
            const data = await res.json();

            if (data.success) {
                setStatuses(data.data);
                setPagination({
                    page: data.page,
                    pageSize: data.pageSize,
                    total: data.total,
                    totalPages: data.totalPages
                });
            }
        } catch (err) {
            console.error("Error while fetching messages:", err);
        }
    };

    useEffect(() => {
        const load = async () => {
            await fetchCampaigns();
        };
        load();
    }, []);

    useEffect(() => {
        if (user && !currentPriceItem) {
            const firstEnabled = apiItems.find((item) => user[item.title.toLowerCase()]);
            if (firstEnabled) {
                setCurrentPriceItem(firstEnabled);
            }
        }
    }, [user, currentPriceItem]);

    useEffect(() => {
        if (activeTab === "apiMessages") {
            const defaultId = getDefaultApiCampaignId();
            setSelectedCampaignId(defaultId);
            setStatuses([]);
            setPagination((prev) => ({ ...prev, page: 1 }));
        }

        if (activeTab === "campaigns") {
            setSelectedCampaignId("");
            setStatuses([]);
            setPagination((prev) => ({ ...prev, page: 1 }));
        }
    }, [activeTab, user]);

    useEffect(() => {
        if (selectedCampaignId && (activeTab === "apiMessages" || activeTab === "campaigns")) {
            fetchStatuses(selectedCampaignId, pagination.page, pagination.pageSize);
        } else {
            setStatuses([]);
        }
    }, [selectedCampaignId, pagination.page, pagination.pageSize, activeTab]);

    if (loading) return <div>Loading...</div>;
    if (!accessToken) return <Navigate to="/login" replace />;
    if (!user) return <div>Loading user data...</div>;
    if (!currentPriceItem) return <div>Loading channels...</div>;

    const parsePhones = async () => {
        if (mode === "manual") {
            return phones
                .split(/[\n,;]/)
                .map((p) => p.replace(/\s+/g, "").trim())
                .filter((p) => /^\+?\d+$/.test(p));
        }

        if (mode === "csv" && csvFile) {
            const text = await csvFile.text();
            return text
                .split(/\r?\n/)
                .map((p) => p.replace(/\s+/g, "").trim())
                .filter((p) => /^\+?\d+$/.test(p));
        }

        return [];
    };

    const handleMessageSubmit = async () => {
        setError(null);
        setSuccess(null);

        const phoneList = await parsePhones();
        if (!message || phoneList.length === 0) {
            setError("Please provide a message and at least one phone number");
            return;
        }

        setLoading(true);

        try {
            const res = await fetchWithAuth("/api/web/campaigns/send", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    phones: phoneList,
                    message,
                    name: "Campaign " + Date.now(),
                    type: currentPriceItem?.title.toLowerCase()
                })
            });

            if (!res) {
                setError("No response from server");
                return;
            }

            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setSuccess("Messages sent successfully!");
                setMessage("");
                setPhones("");
                setCsvFile(null);
                await fetchCampaigns();
                window.dispatchEvent(new Event("force-balance-reload"));
            } else {
                setError(data.error || "Failed to send SMS");
            }
        } catch (err) {
            console.error(err);
            setError("An error occurred while sending the SMS");
        } finally {
            setLoading(false);
        }
    };

    const reloadMessages = async () => {
        if (reloading || !selectedCampaignId) return;
        setReloading(true);

        await Promise.all([
            fetchStatuses(selectedCampaignId, pagination.page, pagination.pageSize),
            new Promise((res) => setTimeout(res, 500))
        ]);

        setReloading(false);
    };

    const backToCampaignsList = () => {
        setSelectedCampaignId("");
        setStatuses([]);
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const allPortalCampaigns = campaigns.filter(
        (c) =>
            c.id !== user?.api_campaign_wa_id &&
            c.id !== user?.api_campaign_wabutton_id &&
            c.id !== user?.api_campaign_telegram_id &&
            c.id !== user?.api_campaign_viber_id &&
            c.id !== user?.api_campaign_imo_id &&
            c.id !== user?.api_campaign_kakaotalk_id
    );

    const portalCampaigns = campaignChannelFilter
        ? allPortalCampaigns.filter((c) => String(c.channel || "").toLowerCase() === campaignChannelFilter)
        : allPortalCampaigns;

    const selectedCampaign = campaigns?.find((c) => String(c.id) === String(selectedCampaignId));

    const formatCampaignDate = (value) => {
        if (!value) return "";

        return new Date(value).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "UTC"
        });
    };

    const getChannelLabel = (channel) => {
        const normalized = String(channel || "").toLowerCase();

        if (normalized === "whatsapp") return "WhatsApp";
        if (normalized === "wabutton") return "WAButton";
        if (normalized === "telegram") return "Telegram";
        if (normalized === "viber") return "Viber";
        if (normalized === "imo") return "IMO";
        if (normalized === "kakaotalk") return "KakaoTalk";

        return channel || "";
    };

    const renderCampaignChannel = (channel) => {
        return (
            <span className="campaignChannel">
                {getChannelLabel(channel)}
            </span>
        );
    };

    const msgColumns = [
        { key: "id", label: "#" },
        { key: "dst_addr", label: "Phone number" },
        { key: "status", label: "Status" },
        { key: "price", label: "Price" },
        { key: "created_at", label: "Created (UTC)" },
        { key: "status_updated_at", label: "Updated (UTC)" },
        { key: "text", label: "Text" }
    ];

    const msgRows = statuses.map((s) => ({
        ...s,
        price: `$${s.price}`,
        created_at: new Date(s.created_at).toLocaleString("en-GB", { timeZone: "UTC" }),
        status_updated_at: new Date(s.status_updated_at).toLocaleString("en-GB", { timeZone: "UTC" }),
        dst_addr: s.dst_addr ? <span title={s.dst_addr}>{s.dst_addr}</span> : "",
        text: s.text ? <span title={s.text}>{`${s.text.slice(0, 30)}...`}</span> : ""
    }));

    const handleExportXlsx = async () => {
        if (!selectedCampaignId) return;

        try {
            const res = await fetchWithAuth(`/api/web/campaign/${selectedCampaignId}/export?format=xlsx`, {
                method: "GET"
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || "Failed to export file");
                return;
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            const today = new Date().toISOString().slice(0, 10);

            a.href = url;
            a.download = `campaign_${selectedCampaignId}_${today}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error("export error", e);
            setError("Export error");
        }
    };

    const handleTabClick = (tabKey) => {
        setActiveTab(tabKey);
        setPortalMenuOpen(false);
    };

    const handlePasswordFieldChange = (field, value) => {
        setPasswordForm((prev) => ({ ...prev, [field]: value }));
        setPasswordMessage(null);
    };

    const handlePasswordSubmit = async (event) => {
        event.preventDefault();

        const currentPassword = passwordForm.currentPassword.trim();
        const newPassword = passwordForm.newPassword.trim();
        const confirmPassword = passwordForm.confirmPassword.trim();

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordMessage({ type: "error", text: "Fill in all password fields." });
            return;
        }

        if (newPassword.length < 4) {
            setPasswordMessage({ type: "error", text: "New password must contain at least 4 characters." });
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordMessage({ type: "error", text: "New password and confirmation do not match." });
            return;
        }

        if (currentPassword === newPassword) {
            setPasswordMessage({ type: "error", text: "New password must be different from current password." });
            return;
        }

        setPasswordSaving(true);
        setPasswordMessage(null);

        try {
            const res = await fetchWithAuth("/api/user/password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ currentPassword, newPassword })
            });

            const data = await res?.json().catch(() => ({}));

            if (res?.ok && data.success) {
                setPasswordForm({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: ""
                });
                setPasswordMessage({ type: "success", text: "Password updated successfully." });
            } else {
                setPasswordMessage({ type: "error", text: data.error || "Failed to update password." });
            }
        } catch (err) {
            console.error("Password update error:", err);
            setPasswordMessage({ type: "error", text: "Server error while updating password." });
        } finally {
            setPasswordSaving(false);
        }
    };

    const renderPortalTabs = (extraClassName = "") => (
        <div className={`portalTabs ${extraClassName}`.trim()}>
            {portalTabs.map((tab) => (
                <button
                    key={tab.key}
                    type="button"
                    className={activeTab === tab.key ? "selectedSendItem portalTabButton" : "sendItem portalTabButton"}
                    onClick={() => handleTabClick(tab.key)}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    );

    return (
        <div className={`portalPage portalTheme-${appTheme}`}>
            <aside className="portalSidebar" aria-label="Portal menu">
                {renderPortalTabs()}
            </aside>

            <div className="portalMobileNav">
                <button
                    type="button"
                    className="portalMobileMenuButton"
                    onClick={() => setPortalMenuOpen((open) => !open)}
                    aria-expanded={portalMenuOpen}
                    aria-controls="portal-mobile-menu"
                >
                    {portalMenuOpen ? <CloseIcon /> : <BurgerIcon />}
                    <span>Portal menu</span>
                </button>

                <div id="portal-mobile-menu" className={portalMenuOpen ? "portalMobilePanel open" : "portalMobilePanel"}>
                    {renderPortalTabs("portalTabsMobile")}
                </div>
            </div>

            <main className="sendContainer portalContent">

                {(activeTab === "dashboard" || activeTab === "api" || activeTab === "support") && (
                    <>
                        <div className="portalHeader">
                            {activeTab === "dashboard" && "Dashboard"}
                            {activeTab === "api" && "API"}
                            {activeTab === "support" && "Support"}
                        </div>
                        <div className="portalUnderDevelopment">
                            This section is under development.
                        </div>
                    </>
                )}

                {(activeTab === "apiMessages" || activeTab === "campaigns") && (
                    <>
                        {activeTab === "apiMessages" && (
                            <div className="portalHeader">API messages</div>
                        )}

                        {activeTab === "campaigns" && (
                            <div className="campaignsHeader">
                                <div className="portalHeader campaignsTitle">Campaigns</div>
                                <button
                                    type="button"
                                    className="campaignCreateButton"
                                    onClick={() => setActiveTab("createCampaign")}
                                >
                                    <PlusIcon className="campaignCreateButtonIcon" />
                                    <span>Create campaign</span>
                                </button>
                            </div>
                        )}

                        {activeTab === "apiMessages" && (
                            <section className="apiMessagesPanel">
                                <div className="apiMessagesPanelHeader">
                                    <div>
                                        <div className="createCampaignSectionTitle">API channel</div>
                                        <div className="createCampaignSectionHint">Select an API stream to inspect delivery statuses.</div>
                                    </div>
                                </div>

                                <div className="apiMessagesChannels">
                                {apiItems.map((item) => {
                                    const campaignId = getApiCampaignIdByItem(item);
                                    const isDisabled = !campaignId;
                                    const isSelected = selectedCampaignId === campaignId;

                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            className={
                                                isSelected
                                                    ? "apiMessagesChannel selected"
                                                    : isDisabled
                                                        ? "apiMessagesChannel disabled"
                                                        : "apiMessagesChannel"
                                            }
                                            onClick={() => {
                                                if (isDisabled) return;
                                                setSelectedCampaignId(campaignId);
                                                setPagination((prev) => ({ ...prev, page: 1 }));
                                            }}
                                            disabled={isDisabled}
                                        >
                                            <span>{item.title}</span>
                                            <span>{campaignId ? "Connected" : "Not connected"}</span>
                                        </button>
                                    );
                                })}
                                </div>
                            </section>
                        )}

                        {activeTab === "campaigns" && !selectedCampaignId && (
                            <>
                                <section className="campaignChannelsPanel">
                                    <div className="apiMessagesPanelHeader">
                                        <div>
                                            <div className="createCampaignSectionTitle">Campaign channel</div>
                                            <div className="createCampaignSectionHint">Filter campaigns by delivery channel.</div>
                                        </div>
                                    </div>

                                    <div className="apiMessagesChannels">
                                        {apiItems.map((item) => {
                                            const channel = item.title.toLowerCase();
                                            const isSelected = campaignChannelFilter === channel;

                                            return (
                                                <button
                                                    key={item.id}
                                                    type="button"
                                                    className={isSelected ? "apiMessagesChannel selected" : "apiMessagesChannel"}
                                                    onClick={() => setCampaignChannelFilter((current) => current === channel ? "" : channel)}
                                                >
                                                    <span>{item.title}</span>
                                                    <span>
                                                        {allPortalCampaigns.filter((c) => String(c.channel || "").toLowerCase() === channel).length} campaigns
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </section>

                                <div className="campaignsTableCard">
                                    <div className="campaignsTableScroll">
                                        <table className="campaignsTable">
                                            <thead>
                                            <tr>
                                                <th>Campaign</th>
                                                <th>Channel</th>
                                                <th>Total</th>
                                                <th>Created</th>
                                                <th>ID</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {portalCampaigns.map((campaign) => (
                                                <tr
                                                    key={campaign.id}
                                                    className={String(selectedCampaignId) === String(campaign.id) ? "selectedCampaignRow" : ""}
                                                    onClick={() => {
                                                        setSelectedCampaignId(String(campaign.id));
                                                        setPagination((prev) => ({ ...prev, page: 1 }));
                                                    }}
                                                >
                                                    <td className="campaignNameCell">{campaign.name}</td>
                                                    <td>{renderCampaignChannel(campaign.channel)}</td>
                                                    <td className="campaignNumberCell">
                                                        {Number(campaign.message_count || 0).toLocaleString("en-US")}
                                                    </td>
                                                    <td>{formatCampaignDate(campaign.created_at)}</td>
                                                    <td className="campaignIdCell">{campaign.id}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {portalCampaigns.length === 0 && (
                                        <div className="campaignsEmptyState">
                                            {campaignChannelFilter ? "No campaigns for this channel." : "No campaigns yet."}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {activeTab === "campaigns" && selectedCampaign && (
                            <div className="campaignDetailsHeader">
                                <button
                                    type="button"
                                    className="campaignBackButton"
                                    onClick={backToCampaignsList}
                                >
                                    <span className="campaignBackIcon">{"<"}</span>
                                    <span>Back to campaigns</span>
                                </button>

                                <div className="campaignDetailsMeta">
                                    <div>
                                        <div className="campaignDetailsName">{selectedCampaign.name}</div>
                                        <div className="campaignDetailsSubline">
                                            {renderCampaignChannel(selectedCampaign.channel)}
                                            <span>{Number(selectedCampaign.message_count || 0).toLocaleString("en-US")} messages</span>
                                            <span>{formatCampaignDate(selectedCampaign.created_at)}</span>
                                        </div>
                                    </div>
                                    <span className="campaignDetailsId">#{selectedCampaign.id}</span>
                                </div>
                            </div>
                        )}

                        {activeTab === "campaigns" && selectedCampaign?.message && (
                            <section className="campaignMessagePanel">
                                <div className="campaignMessageTitle">Message</div>
                                <div className="campaignMessageText">{selectedCampaign.message}</div>
                            </section>
                        )}

                        {selectedCampaignId && (
                            <section className="messagesTablePanel">
                                <div className="tableToolbar">
                                    <div className="tableFilterGroup">
                                    <select
                                        value={msgFilters.status}
                                        onChange={(e) => setMsgFilters((f) => ({ ...f, status: e.target.value }))}
                                    >
                                        <option value="">Status: any</option>
                                        <option value="checking">checking</option>
                                        <option value="sent">sent</option>
                                        <option value="delivered">delivered</option>
                                        <option value="failed">failed</option>
                                        <option value="undelivered">undelivered</option>
                                        <option value="noaccount">noaccount</option>
                                        <option value="expired">expired</option>
                                    </select>

                                    <input
                                        type="date"
                                        value={msgFilters.date_from}
                                        onChange={(e) => setMsgFilters((f) => ({ ...f, date_from: e.target.value }))}
                                    />

                                    <input
                                        type="date"
                                        value={msgFilters.date_to}
                                        onChange={(e) => setMsgFilters((f) => ({ ...f, date_to: e.target.value }))}
                                    />

                                    <input
                                        placeholder="Phone contains..."
                                        value={msgFilters.phone}
                                        onChange={(e) => setMsgFilters((f) => ({ ...f, phone: e.target.value }))}
                                    />

                                    <button className="tableToolbarButton" onClick={() => fetchStatuses(selectedCampaignId, 1, pagination.pageSize)}>Apply</button>

                                    <button
                                        className="tableToolbarButton tableToolbarButtonSecondary"
                                        onClick={() => {
                                            const empty = {
                                                status: "",
                                                date_from: "",
                                                date_to: "",
                                                phone: ""
                                            };
                                            setMsgFilters(empty);

                                            const qs = new URLSearchParams({
                                                page: "1",
                                                limit: String(pagination.pageSize)
                                            }).toString();

                                            fetchWithAuth(`/api/web/campaign/${selectedCampaignId}?${qs}`)
                                                .then((r) => r.json())
                                                .then((data) => {
                                                    if (data.success) {
                                                        setStatuses(data.data);
                                                        setPagination({
                                                            page: data.page,
                                                            pageSize: data.pageSize,
                                                            total: data.total,
                                                            totalPages: data.totalPages
                                                        });
                                                    }
                                                });
                                        }}
                                    >
                                        Reset
                                    </button>
                                    </div>

                                    <div className="tableActionGroup">
                                        <button
                                            type="button"
                                            onClick={reloadMessages}
                                            disabled={reloading || !selectedCampaignId}
                                            className="tableActionButton tableActionButtonSecondary"
                                        >
                                            <Refresh className={reloading ? "tableActionIcon tableActionIconSpin" : "tableActionIcon"} />
                                            <span>Refresh</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleExportXlsx}
                                            disabled={!selectedCampaignId}
                                            className="tableActionButton"
                                        >
                                            Export info
                                        </button>
                                    </div>
                                </div>

                                <Table height="400px" columns={msgColumns} rows={msgRows} />

                                <div className="tablePagination">
                                    <button
                                        disabled={pagination.page === 1}
                                        onClick={() => fetchStatuses(selectedCampaignId, pagination.page - 1, pagination.pageSize)}
                                    >
                                        Prev
                                    </button>

                                    <span>
                                        Page {pagination.page} of {pagination.totalPages} ({pagination.total} messages)
                                    </span>

                                    <button
                                        disabled={pagination.page === pagination.totalPages}
                                        onClick={() => fetchStatuses(selectedCampaignId, pagination.page + 1, pagination.pageSize)}
                                    >
                                        Next
                                    </button>

                                    <select
                                        value={pagination.pageSize}
                                        onChange={(e) => fetchStatuses(selectedCampaignId, 1, Number(e.target.value))}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </section>
                        )}
                    </>
                )}

                {activeTab === "createCampaign" && (
                    <>
                        <div className="portalHeader">Create campaign</div>

                        <section className="createCampaignPanel">
                            <div className="createCampaignSection">
                                <div className="createCampaignSectionHeader">
                                    <div>
                                        <div className="createCampaignSectionTitle">Channel</div>
                                        <div className="createCampaignSectionHint">Choose where this campaign will be sent.</div>
                                    </div>
                                    <div className="createCampaignPrice">
                                        ${+user[apiItems[currentPriceItem.id].priceField]} per message
                                    </div>
                                </div>

                                <div className="createCampaignChannels">
                                    {apiItems.map((item) => {
                                        const isDisabled = !user[item.title.toLowerCase()];
                                        const isSelected = currentPriceItem?.id === item.id;

                                        return (
                                            <button
                                                key={item.id}
                                                type="button"
                                                className={
                                                    isSelected
                                                        ? "createCampaignChannel selected"
                                                        : isDisabled
                                                            ? "createCampaignChannel disabled"
                                                            : "createCampaignChannel"
                                                }
                                                onClick={() => setCurrentPriceItem(item)}
                                                disabled={isDisabled}
                                            >
                                                <span>{item.title}</span>
                                                <span>${+user[item.priceField]}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="createCampaignSection">
                                <div className="createCampaignSectionHeader">
                                    <div>
                                        <div className="createCampaignSectionTitle">Recipients</div>
                                        <div className="createCampaignSectionHint">Add phone numbers manually or import a CSV file.</div>
                                    </div>
                                </div>

                                <div className="createCampaignModeSwitch">
                                    <button
                                        type="button"
                                        className={mode === "manual" ? "createCampaignMode selected" : "createCampaignMode"}
                                        onClick={() => setMode("manual")}
                                    >
                                        Enter manually
                                    </button>
                                    <button
                                        type="button"
                                        className={mode === "csv" ? "createCampaignMode selected" : "createCampaignMode"}
                                        onClick={() => setMode("csv")}
                                    >
                                        Upload CSV
                                    </button>
                                </div>

                                {mode === "manual" ? (
                                    <div className="createCampaignField">
                                        <label className="labelP">Phone list</label>
                                        <textarea
                                            className="blockGeneratorContentTextareaBlockTextarea createCampaignTextarea"
                                            placeholder="Enter numbers separated by commas or line breaks"
                                            value={phones}
                                            onChange={(e) => setPhones(e.target.value)}
                                            disabled={isLoading}
                                        />
                                        <div className="createCampaignFieldHint">
                                            Example: <code>1234567890, 9876543210, 5556667777</code>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="createCampaignField">
                                        <label className="labelP">Phone list</label>
                                        <label className="customFileUpload createCampaignFileUpload">
                                            <img alt="upload" className="imageU" src={upload} />
                                            <div className="fileName">{csvFile ? csvFile.name : "Select file"}</div>
                                            <div className="fileFormat">CSV format</div>
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={(e) => {
                                                    if (e.target.files.length > 0) {
                                                        setCsvFile(e.target.files[0]);
                                                    }
                                                }}
                                                disabled={isLoading}
                                            />
                                        </label>
                                    </div>
                                )}
                            </div>

                            <div className="createCampaignSection createCampaignSectionLast">
                                <div className="createCampaignSectionHeader">
                                    <div>
                                        <div className="createCampaignSectionTitle">Message</div>
                                        <div className="createCampaignSectionHint">Write the text that recipients will receive.</div>
                                    </div>
                                </div>

                                <div className="createCampaignField">
                                    <label className="labelP">Message text</label>
                                    <textarea
                                        className="blockGeneratorContentTextareaBlockTextarea createCampaignTextarea createCampaignMessageTextarea"
                                        placeholder="Message text"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                            <div className="createCampaignFooter">
                                <div className="createCampaignSummary">
                                    <span>Selected channel</span>
                                    <strong>{currentPriceItem.title}</strong>
                                </div>
                                <button onClick={handleMessageSubmit} className="startCampaignButton" disabled={isLoading}>
                                    <div className="buttonMode">{isLoading ? "Sending" : "Create campaign"}</div>
                                </button>
                            </div>

                            {error && <div className="createCampaignAlert">{error}</div>}
                            {success && <div className="createCampaignAlert success">{success}</div>}
                        </section>
                    </>
                )}

                {activeTab === "balance" && (
                    <>
                        <div className="portalHeader">Balance</div>
                        <PaymentWidget />
                    </>
                )}

                {activeTab === "settings" && (
                    <>
                        <div className="portalHeader">Settings</div>

                        <section className="settingsPanel">
                            <form className="settingsCard" onSubmit={handlePasswordSubmit}>
                                <div className="settingsCardHeader">
                                    <div>
                                        <div className="createCampaignSectionTitle">Change password</div>
                                        <div className="createCampaignSectionHint">Update the password used to sign in to Portal.</div>
                                    </div>
                                </div>

                                <div className="settingsAccountSummary">
                                    <div className="settingsSummaryItem">
                                        <span>Account login</span>
                                        <strong>{user.username}</strong>
                                    </div>
                                    <div className="settingsSummaryItem">
                                        <span>Sign-in method</span>
                                        <strong>Password</strong>
                                    </div>
                                </div>

                                <div className="settingsDivider" />

                                <div className="settingsFormGrid">
                                    <label className="settingsField">
                                        <span>Current password</span>
                                        <input
                                            type="password"
                                            value={passwordForm.currentPassword}
                                            onChange={(e) => handlePasswordFieldChange("currentPassword", e.target.value)}
                                            autoComplete="current-password"
                                            disabled={passwordSaving}
                                        />
                                    </label>

                                    <label className="settingsField">
                                        <span>New password</span>
                                        <input
                                            type="password"
                                            value={passwordForm.newPassword}
                                            onChange={(e) => handlePasswordFieldChange("newPassword", e.target.value)}
                                            autoComplete="new-password"
                                            disabled={passwordSaving}
                                        />
                                    </label>

                                    <label className="settingsField">
                                        <span>Confirm new password</span>
                                        <input
                                            type="password"
                                            value={passwordForm.confirmPassword}
                                            onChange={(e) => handlePasswordFieldChange("confirmPassword", e.target.value)}
                                            autoComplete="new-password"
                                            disabled={passwordSaving}
                                        />
                                    </label>
                                </div>

                                <button type="submit" className="settingsSaveButton" disabled={passwordSaving}>
                                    {passwordSaving ? "Updating..." : "Update password"}
                                </button>

                                {passwordMessage && (
                                    <div className={`settingsMessage ${passwordMessage.type}`}>
                                        {passwordMessage.text}
                                    </div>
                                )}
                            </form>
                        </section>
                    </>
                )}
            </main>
        </div>
    );
}
